import { useState, useEffect, useCallback } from 'react';
import {
  sponsorService,
  SponsorSettings,
  UpdateSponsorSettingsRequest,
  CompanyWithSponsorSettings,
} from '../services/sponsorService';
import { useToast } from './useToast';

export interface UseSponsorSettingsReturn {
  // 単一企業のスポンサー設定
  settings: SponsorSettings | null;
  loading: boolean;
  error: string | null;

  // 設定更新
  updateSettings: (settings: UpdateSponsorSettingsRequest) => Promise<void>;

  // 設定取得
  fetchSettings: (companyId: number) => Promise<void>;
}

export interface UseAllSponsorSettingsReturn {
  // 全企業のスポンサー設定
  companies: CompanyWithSponsorSettings[];
  loading: boolean;
  error: string | null;

  // ページネーション
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // 検索・フィルター
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // アクション
  fetchCompanies: (page?: number, search?: string) => Promise<void>;
  updateCompanySponsorSettings: (
    companyId: number,
    settings: UpdateSponsorSettingsRequest
  ) => Promise<void>;

  // ページ操作
  goToPage: (page: number) => void;
  search: () => void;
  resetSearch: () => void;
}

/**
 * 単一企業のスポンサー設定管理hook
 */
export const useSponsorSettings = (
  companyId?: number
): UseSponsorSettingsReturn => {
  const [settings, setSettings] = useState<SponsorSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const fetchSettings = useCallback(
    async (targetCompanyId: number) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await sponsorService.getSponsorSettings(targetCompanyId);
        setSettings(response.data);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'スポンサー設定の取得に失敗しました';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  const updateSettings = useCallback(
    async (newSettings: UpdateSponsorSettingsRequest) => {
      if (!settings) return;

      setLoading(true);
      setError(null);

      try {
        const response = await sponsorService.updateSponsorSettings(
          settings.companyId,
          newSettings
        );
        setSettings(response.data);
        showToast('スポンサー設定を更新しました', 'success');
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'スポンサー設定の更新に失敗しました';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [settings, showToast]
  );

  // 初期データ取得
  useEffect(() => {
    if (companyId) {
      fetchSettings(companyId);
    }
  }, [companyId, fetchSettings]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    fetchSettings,
  };
};

/**
 * 全企業のスポンサー設定管理hook（管理者専用）
 */
export const useAllSponsorSettings = (): UseAllSponsorSettingsReturn => {
  const [companies, setCompanies] = useState<CompanyWithSponsorSettings[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const { showToast } = useToast();

  const fetchCompanies = useCallback(
    async (page: number = 1, search?: string) => {
      setLoading(true);
      setError(null);

      try {
        const response =
          await sponsorService.getAllCompaniesWithSponsorSettings(
            page,
            pagination.limit,
            search
          );

        setCompanies(response.data.companies);
        setPagination(response.data.pagination);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '企業一覧の取得に失敗しました';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, showToast]
  );

  const updateCompanySponsorSettings = useCallback(
    async (companyId: number, settings: UpdateSponsorSettingsRequest) => {
      try {
        await sponsorService.updateSponsorSettings(companyId, settings);

        // ローカル状態を更新
        setCompanies((prev) =>
          prev.map((company) =>
            company.id === companyId
              ? {
                  ...company,
                  isSponsor: settings.isSponsor,
                  updatedAt: new Date().toISOString(),
                  logoUrl: company.logoUrl,
                }
              : company
          )
        );

        showToast('スポンサー設定を更新しました', 'success');
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'スポンサー設定の更新に失敗しました';
        showToast(errorMessage, 'error');
        throw err;
      }
    },
    [showToast]
  );

  const goToPage = useCallback(
    (page: number) => {
      fetchCompanies(page, searchQuery);
    },
    [fetchCompanies, searchQuery]
  );

  const search = useCallback(() => {
    fetchCompanies(1, searchQuery);
  }, [fetchCompanies, searchQuery]);

  const resetSearch = useCallback(() => {
    setSearchQuery('');
    fetchCompanies(1, '');
  }, [fetchCompanies]);

  // 初期データ取得
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  return {
    companies,
    loading,
    error,
    pagination,
    searchQuery,
    setSearchQuery,
    fetchCompanies,
    updateCompanySponsorSettings,
    goToPage,
    search,
    resetSearch,
  };
};
